'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import { jwtDecode } from 'jwt-decode';
import { useSession } from 'next-auth/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { usePostHog } from '~/hooks/usePostHog';
import { LocaleEnum } from '~/locales/resources';
import { SessionUserType } from '~/types-and-enums/trackingTypes';
import { checkUserIsEmployee } from '~/utils/generalUtils';

type JwtClaims = {
  contact_id?: string;
  account_id?: string;
  account_name?: string;
  finished_setup?: boolean;
};

type Props = {
  locale?: LocaleEnum;
};

const Tracking = ({ locale }: Props) => {
  const { data: session } = useSession();
  const user = session?.user as SessionUserType;
  const {
    capturePageView,
    identifyUser: posthogIdentify,
    isEnabled: isPostHogEnabled,
  } = usePostHog();

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const previousPathRef = useRef<string | null>(null);

  const fullPath = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;

  // GTM + PostHog user identification on session load
  useEffect(() => {
    if (!user) return;
    const { id, name, email, access_token } = user;
    const isEmployee = checkUserIsEmployee(email);

    if (process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID) {
      sendGTMEvent({
        event: 'E_onLayoutLoad',
        user_id: id,
        user_name: name,
        user_email: email,
        user_isEmployee: isEmployee,
        user_locale: locale,
        ...(isEmployee && { traffic_type: 'internal' }),
      });
    }

    if (id) {
      let jwtClaims: JwtClaims = {};
      try {
        if (access_token) jwtClaims = jwtDecode<JwtClaims>(access_token);
      } catch {}
      posthogIdentify(id, {
        email: email || '',
        name: name || '',
        user_type: isEmployee ? 'employee' : 'buyer',
        is_employee: isEmployee,
        salesforce_contact_id: jwtClaims.contact_id || '',
        salesforce_account_id: jwtClaims.account_id || '',
        company_name: jwtClaims.account_name || '',
        finished_setup: jwtClaims.finished_setup ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, locale]);

  // PostHog page view on SPA route changes
  useEffect(() => {
    if (fullPath !== previousPathRef.current) {
      if (isPostHogEnabled) {
        capturePageView(typeof window !== 'undefined' ? window.location.href : fullPath);
        previousPathRef.current = fullPath;
      }
    }
  }, [fullPath, isPostHogEnabled, capturePageView]);

  return null;
};

export default Tracking;
